// Full mock implementation of the buyer api — offline dev fallback when
// `BUN_PUBLIC_API_GATEWAY_URL` is unset. Mirrors the gateway's behaviour
// (published-only search + facets + sort, per-shop storefront, server-style
// cart + orders with mock payment + stock decrement) over ./data.ts fixtures.
import type {
	AddCartItemRequest,
	Api,
	BoutiqueConfig,
	BuyerAddress,
	BuyerAuthStartResponse,
	BuyerAuthVerifyResponse,
	BuyerMe,
	BuyerMessage,
	BuyerPaymentMethod,
	BuyerProfile,
	BuyerSession,
	Cart,
	CartItem,
	CategoryFacet,
	ChatConversation,
	ChatMessage,
	CreateOrderRequest,
	DeliveryConfig,
	DeliveryMethod,
	DeliveryOption,
	FacetValue,
	LikeState,
	DeviceAttachResult,
	Order,
	OrderGroup,
	PaginatedLikeIds,
	Product,
	ProductFacets,
	ProductManufacturingRequest,
	ProductPage,
	ProductQuery,
	Shop,
	Storefront,
	User,
	BuyerAddressInput,
	BuyerRecipient,
	SavedPickupPoint,
	BuyerPaymentMethodInput,
	BuyerPaymentMethodPatch,
	BuyerBalance,
	LedgerEntry,
	BalanceTopup,
	TopupConfirmResult,
	BillingProfile,
	BillingProfileInput,
	Invoice,
	BuyerProfilePatch,
	BuyerRequestAttachment,
	BuyerRequestDetail,
	BuyerRequestList,
	CreateBuyerRequest,
	RequestOffer,
	UpdateBuyerRequest,
	FaqList,
	SupportTicket,
	SupportTicketDetail,
	SupportTicketMessage,
	CreateTicketRequest,
	PartnerProduct,
	MetrikaConfig,
} from "../types";
import { sleep } from "@/lib/utils";
import { getBuyerAuthToken } from "../token";
import { mockArchivedCategories, mockCategories, mockCategoryById, mockProducts, mockShops, mockSubtreeIds } from "./data";
import { mockCountries, mockCurrencies, suggestMockAddresses, suggestMockCities } from "./geo";
import { buildFeedBlocks, mockFeedCollections } from "./feed";
import { ensureMockPurchaseReceipt, mockNotifications } from "./notifications";
import { mockProductReviews } from "./reviews";
import { mockCabinet } from "./cabinet";
import { createPromosMock } from "../promos";
import { createLooksMock } from "../looks";
import { createUgcMock } from "../ugc";
import { looksLikeUgcArticle } from "@osmix/contracts/ugc-articles";
import {
	CreateShopCustomOrderSchema,
	estimateShopCustomOrderPrice,
	type ShopCustomOrder,
} from "@osmix/contracts/shop-custom-order";
import { groupSizeFacet } from "@osmix/catalog-core";
import type {
	ProductReview,
	ProductReviewList,
	ProductReviewSort,
	ProductReviewsQuery,
} from "../types";

const products: Product[] = mockProducts.map((p) => ({ ...p }));
const newId = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 12)}`;
const byId = (id: string) => products.find((p) => p.id === id);
// Карточка товара во вложении сообщения чата — как её собирает шлюз.
const mockProductAttachment = (p: Product) => ({
	kind: "product",
	productId: p.id,
	productName: p.name,
	productPhoto: p.photos[0],
	priceLabel: `${p.price.toLocaleString("ru-RU")} ₽`,
});
const published = () => products.filter((p) => p.status === "published" && p.stock > 0);
const lower = (s: string) => s.toLowerCase();

const matchesSearch = (p: Product, q: string) => {
	const t = lower(q);
	return (
		lower(p.name).includes(t) ||
		lower(p.article).includes(t) ||
		lower(p.brand).includes(t) ||
		p.keywords.some((k) => lower(k).includes(t))
	);
};

const counts = (values: string[]): FacetValue[] => {
	const map = new Map<string, number>();
	for (const v of values) map.set(v, (map.get(v) ?? 0) + 1);
	return [...map.entries()]
		.map(([value, count]) => ({ value, count }))
		.sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
};

// Category facets mirror the managed tree: nest children under parents, roll
// each parent's count up over its subcategories, and drop empty nodes at every
// level. Value = category id, label = tree name.
const categoryFacets = (list: Product[]): CategoryFacet[] => {
	const direct = new Map<string, number>();
	for (const p of list) {
		if (p.categoryId) direct.set(p.categoryId, (direct.get(p.categoryId) ?? 0) + 1);
	}
	// Дерево любой глубины (v2 — три уровня): счётчик узла — его товары плюс
	// товары потомков; пустые узлы выброшены на каждом уровне, как у шлюза.
	const facetOf = (node: (typeof mockCategories)[number]): CategoryFacet => {
		const kids = (node.children ?? []).map(facetOf).filter((k) => k.count > 0);
		const count = (direct.get(node.id) ?? 0) + kids.reduce((s, k) => s + k.count, 0);
		return { value: node.id, label: node.name, count, children: kids.length ? kids : undefined };
	};
	return mockCategories.map(facetOf).filter((c) => c.count > 0);
};

const buildStorefront = (shop: Shop): Storefront => {
	const ids = published()
		.filter((p) => p.shopId === shop.id)
		.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
		.map((p) => p.id);
	const half = Math.ceil(ids.length / 2);
	const config: BoutiqueConfig = {
		header: { logoUrl: shop.logoUrl, description: shop.description, customOrdersEnabled: true },
		about: { enabled: true, titlePageEnabled: false, blocks: [] },
		banner: {
			enabled: true,
			slides: [
				{ id: `${shop.id}-banner`, imageUrl: shop.heroUrl, title: shop.name, description: shop.tagline },
			],
		},
		pinnedProducts: ids.slice(0, 3),
		collections: {
			groupByCollection: true,
			items:
				ids.length > 3
					? [
							{ id: `${shop.id}-col-1`, title: "Бестселлеры", productIds: ids.slice(0, half) },
							{ id: `${shop.id}-col-2`, title: "Новинки", productIds: ids.slice(half) },
						]
					: [],
		},
		partnerProducts: [],
		interactiveBlocks: [
			{ id: `${shop.id}-about`, type: "about-brand", heading: shop.name, body: shop.description, photoUrl: shop.heroUrl },
		],
		published: true,
		updatedAt: new Date().toISOString(),
	};
	const productCount = ids.length;
	// fd-boutique-12: партнёрский блок витрины — товары ДРУГИХ магазинов с
	// атрибуцией источника. Берём до четырёх опубликованных карточек соседей.
	const partnerCatalog: PartnerProduct[] = published()
		.filter((p) => p.shopId !== shop.id)
		.slice(0, 4)
		.map((product) => ({
			product,
			partnerShopId: product.shopId,
			partnerShopName: shopMeta.get(product.shopId)?.name,
		}));
	return {
		shop: { ...shop, productCount },
		config,
		partnerCatalog: partnerCatalog.length ? partnerCatalog : undefined,
	};
};

// ── server-style cart (in-memory) ─────────────────────────────────────────────
type StoredItem = { id: string; productId: string; variantId?: string; size: string; color: string; qty: number };
type StoredCart = { id: string; items: StoredItem[]; updatedAt: string; promoCode?: string };

// «Скидка OSMIX»: демо-промокоды офлайн-мока (зеркалит сид миграции 056).
const MOCK_PROMOS: Record<string, { kind: "percent" | "fixed"; value: number; minSubtotal: number }> = {
	OSMIX10: { kind: "percent", value: 0.1, minSubtotal: 0 },
};
const mockPromoDiscount = (code: string | undefined, subtotal: number): number => {
	const promo = code ? MOCK_PROMOS[code] : undefined;
	if (!promo || subtotal < promo.minSubtotal) return 0;
	const raw = promo.kind === "percent" ? subtotal * promo.value : promo.value;
	return round2(Math.min(Math.max(raw, 0), subtotal));
};
const carts = new Map<string, StoredCart>();

const variantLabel = (variant: NonNullable<Product["variants"]>[number]): string => {
	const parts = [variant.color, variant.size, ...Object.values(variant.options ?? {})]
		.map((part) => part.trim())
		.filter(Boolean);
	return [...new Set(parts)].join(" / ");
};

const positiveDimension = (value: number | undefined): number | undefined =>
	value != null && Number.isFinite(value) && value > 0 ? value : undefined;

const hydrate = (cart: StoredCart): Cart => {
	const items: CartItem[] = [];
	for (const it of cart.items) {
		const p = byId(it.productId);
		if (!p) continue;
		const variant = it.variantId ? p.variants?.find((v) => v.id === it.variantId) : undefined;
		const unitPrice = variant?.price ?? p.price;
		const oldUnitPrice = variant?.oldPrice ?? p.oldPrice;
		const stock = variant?.stock ?? p.stock;
		items.push({
			id: it.id,
			productId: p.id,
			shopId: p.shopId,
			name: p.name,
			brand: p.brand,
			photo: variant?.photos[0] ?? p.photos[0] ?? "",
			variantId: variant?.id,
			variantLabel: variant ? variantLabel(variant) : undefined,
			options: variant?.options,
			size: variant?.size ?? it.size,
			color: variant?.color ?? it.color,
			qty: it.qty,
			unitPrice,
			oldUnitPrice: oldUnitPrice && oldUnitPrice > unitPrice ? oldUnitPrice : undefined,
			lineTotal: unitPrice * it.qty,
			stock,
			madeToOrder: Boolean(p.customization?.enabled),
			weightGrams: positiveDimension(variant?.weightGrams ?? p.weightGrams),
			lengthCm: positiveDimension(variant?.lengthCm ?? p.lengthCm),
			widthCm: positiveDimension(variant?.widthCm ?? p.widthCm),
			heightCm: positiveDimension(variant?.heightCm ?? p.heightCm),
			available: p.status === "published" && stock >= it.qty,
		});
	}
	const subtotal = items.reduce((s, i) => s + i.lineTotal, 0);
	const discount = mockPromoDiscount(cart.promoCode, subtotal);
	return {
		id: cart.id,
		items,
		itemCount: items.reduce((s, i) => s + i.qty, 0),
		subtotal,
		discount,
		promoCode: discount > 0 ? cart.promoCode : undefined,
		updatedAt: cart.updatedAt,
	};
};

const mustCart = (id: string): StoredCart => {
	const c = carts.get(id);
	if (!c) throw new Error("Cart not found");
	return c;
};

// Частичное оформление (как в шлюзе): только выбранные строки корзины.
const pickLines = (cart: Cart, lineIds?: string[]): Cart => {
	if (!lineIds?.length) return cart;
	const set = new Set(lineIds);
	const items = cart.items.filter((i) => set.has(i.id));
	return { ...cart, items, itemCount: items.reduce((s, i) => s + i.qty, 0), subtotal: items.reduce((s, i) => s + i.lineTotal, 0), discount: 0, promoCode: undefined };
};

// Delivery pricing the mock serves via settings.delivery() — orders read it
// rather than hardcoding, mirroring the gateway's configurable settings.
const deliveryConfig: DeliveryConfig = {
	fees: { standard: 300, express: 700, pickup: 0 },
	freeOver: 15000,
	updatedAt: new Date().toISOString(),
};
const shopMeta = new Map(mockShops.map((s) => [s.id, s]));
const orders = new Map<string, Order>();

// Flat delivery options for a shop group — the mock has no live carrier, so
// every quote is the flat fallback (mirrors the gateway's flatOptions).
const flatOptions = (subtotal: number): DeliveryOption[] => {
	const fee = (m: DeliveryMethod) => (subtotal >= deliveryConfig.freeOver ? 0 : deliveryConfig.fees[m]);
	return [
		{ carrier: "flat", method: "standard", label: "Стандартная доставка", deliverySum: fee("standard") },
		{ carrier: "flat", method: "express", label: "Экспресс-доставка", deliverySum: fee("express") },
		{ carrier: "flat", method: "pickup", label: "Самовывоз", deliverySum: fee("pickup"), toPickupPoint: true },
	];
};

const deriveMethod = (groups: OrderGroup[]): DeliveryMethod => {
	const ships = groups.map((g) => g.shipment).filter((s): s is NonNullable<typeof s> => Boolean(s));
	if (ships.length && ships.every((s) => s.toPickupPoint)) return "pickup";
	if (ships.some((s) => s.method === "express")) return "express";
	return "standard";
};
// Which cart each pending order came from, so confirm can drop it (mirrors the
// gateway's orders.cart_id column).
const orderCarts = new Map<string, string>();
const likedProducts = new Map<string, Set<string>>();
const likedShops = new Map<string, Set<string>>();
const likedIdeas = new Map<string, Set<string>>();
const setFor = (map: Map<string, Set<string>>, clientId: string) => {
	const existing = map.get(clientId);
	if (existing) return existing;
	const next = new Set<string>();
	map.set(clientId, next);
	return next;
};

const mockUser: User = {
	id: "buyer-demo",
	email: "buyer@osmix.test",
	phone: "+79995554433",
	name: "Анна Покупатель",
	role: "buyer",
	onboardingStatus: "active",
};
let mockProfile: BuyerProfile = {
	userId: mockUser.id,
	name: mockUser.name,
	email: mockUser.email,
	phone: mockUser.phone,
	birthDate: "1992-04-12",
	createdAt: new Date().toISOString(),
	updatedAt: new Date().toISOString(),
};
let currentAuthSession: BuyerAuthStartResponse | null = null;
let mockPendingPhone: string | null = null;
const MOCK_SBP_BANKS = [
	{ id: "sberbank", name: "Сбербанк", logoUrl: "" },
	{ id: "tbank", name: "Т-Банк", logoUrl: "" },
	{ id: "vtb", name: "ВТБ", logoUrl: "" },
	{ id: "alfabank", name: "Альфа-Банк", logoUrl: "" },
	{ id: "ozonbank", name: "Озон Банк", logoUrl: "" },
];
let currentBuyerToken: string | null = null;
const buyerSessions = new Map<string, BuyerSession>();
const buyerAddresses = new Map<string, BuyerAddress>();
const buyerRecipients = new Map<string, BuyerRecipient>();
const buyerPickupPoints = new Map<string, SavedPickupPoint>();
const buyerPaymentMethods = new Map<string, BuyerPaymentMethod>();
// Внутренний баланс + леджер + пополнения (fd-payment-03). Валюта — базовая RUB,
// как у платёжного контура площадки.
const BALANCE_CURRENCY = "RUB";
let buyerBalanceAmount = 0;
let buyerBalanceUpdatedAt = new Date(0).toISOString();
const buyerLedger: LedgerEntry[] = [];
const buyerTopups = new Map<string, BalanceTopup>();
// Реквизиты ЮЛ покупателя для оплаты по счёту (fd-payment-02).
let buyerBillingProfile: BillingProfile | null = null;
// Реквизиты площадки для счёта — по умолчанию только название (как в gateway).
const mockPlatformRequisites = {
	companyName: "OSMIX",
	inn: "",
	kpp: "",
	ogrn: "",
	legalAddress: "",
	bankName: "",
	bankAccount: "",
	corrAccount: "",
	bik: "",
};
const round2 = (v: number): number => Math.round(v * 100) / 100;
const pushLedger = (delta: number, kind: LedgerEntry["kind"], comment: string, ref?: { refType: string; refId: string }): void => {
	buyerBalanceAmount = round2(buyerBalanceAmount + delta);
	buyerBalanceUpdatedAt = nowIso();
	buyerLedger.unshift({
		id: newId("bl"),
		userId: mockUser.id,
		delta: round2(delta),
		kind,
		refType: ref?.refType,
		refId: ref?.refId,
		balanceAfter: buyerBalanceAmount,
		comment,
		createdAt: nowIso(),
	});
};
const buyerOrderIds = new Set<string>();
const buyerMessages: BuyerMessage[] = [];
const buyerConversations = new Map<string, ChatConversation>();
// fd-boutique-13: заявки на индивидуальный заказ с витрины (как shop_custom_orders).
const buyerCustomOrders = new Map<string, ShopCustomOrder>();
// Запросы покупателя на поиск и изготовление (fd-request-*).
const buyerRequests = new Map<string, BuyerRequestDetail>();
let requestSeq = 0;
const nextRequestCode = () => `R-${String(++requestSeq).padStart(4, "0")}`;
const mockCategoryName = (id: string): string | undefined => mockCategoryById(id)?.name;
const productManufacturingRequests: ProductManufacturingRequest[] = [];
const buyerProductLikes = new Set<string>();
const buyerShopLikes = new Set<string>();
const buyerIdeaLikes = new Set<string>();
const buyerReviewLikes = new Set<string>();
// Вопросы к товарам в офлайн-режиме: по товару, новые сверху.
const productQuestionsStore = new Map<string, import("@osmix/contracts/product-social").ProductQuestion[]>();
// «Скрыть идею» (A-26): гостевые скрытия по clientId и скрытия аккаунта.
const hiddenIdeasByClient = new Map<string, Set<string>>();
const buyerHiddenIdeas = new Set<string>();

// Отзывы карточки товара (fd-review-02). Мутабельные копии фикстур.
const productReviewsStore = new Map<string, ProductReview[]>(
	Object.entries(mockProductReviews).map(([id, list]) => [id, list.map((r) => ({ ...r }))]),
);

const summarizeReviews = (items: ProductReview[]): ProductReviewList["summary"] => {
	const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<1 | 2 | 3 | 4 | 5, number>;
	for (const item of items) distribution[item.rating as 1 | 2 | 3 | 4 | 5]++;
	const count = items.length;
	const average = count === 0 ? 0 : items.reduce((sum, item) => sum + item.rating, 0) / count;
	// «Продано» и «возвратов» считает гейтвей по заказам; офлайн-мок их не
	// выдумывает — нулями он честно говорит «источника нет».
	return { average: Math.round(average * 10) / 10, count, distribution, soldCount: 0, returnCount: 0 };
};

const nowIso = () => new Date().toISOString();
const requireBuyer = (): BuyerMe => {
	if (!currentBuyerToken || getBuyerAuthToken() !== currentBuyerToken) throw new Error("Sign in required");
	mockProfile = { ...mockProfile, updatedAt: mockProfile.updatedAt };
	return { user: { ...mockUser, name: mockProfile.name, email: mockProfile.email ?? mockUser.email }, profile: mockProfile };
};
const paginateIds = (ids: string[], input: { page?: number; pageSize?: number } = {}): PaginatedLikeIds => {
	const page = Math.max(1, input.page ?? 1);
	const pageSize = Math.min(100, Math.max(1, input.pageSize ?? 20));
	return { ids: ids.slice((page - 1) * pageSize, page * pageSize), total: ids.length, page, pageSize };
};
const countProductLikes = (productId: string) => {
	let likes = buyerProductLikes.has(productId) ? 1 : 0;
	for (const ids of likedProducts.values()) if (ids.has(productId)) likes++;
	return likes;
};
const countShopLikes = (shopId: string) => {
	let likes = buyerShopLikes.has(shopId) ? 1 : 0;
	for (const ids of likedShops.values()) if (ids.has(shopId)) likes++;
	return likes;
};
const countIdeaLikes = (ideaId: string) => {
	let likes = buyerIdeaLikes.has(ideaId) ? 1 : 0;
	for (const ids of likedIdeas.values()) if (ids.has(ideaId)) likes++;
	return likes;
};
const upsertMockConversation = (product: Product, body: string, requestId?: string): ChatConversation => {
	const existing = [...buyerConversations.values()].find((item) =>
		requestId ? item.manufacturingRequestId === requestId : item.productId === product.id && !item.manufacturingRequestId,
	);
	const now = nowIso();
	const conversation: ChatConversation = existing ?? {
		id: newId("chat"),
		shopId: product.shopId,
		shopName: shopMeta.get(product.shopId)?.name,
		buyerUserId: mockUser.id,
		buyerName: mockProfile.name,
		productId: product.id,
		productName: product.name,
		manufacturingRequestId: requestId,
		subject: product.name,
		context: requestId ? { kind: "manufacturing_request", requestId } : { kind: "product" },
		lastMessageAt: now,
		unreadCount: 0,
		createdAt: now,
		updatedAt: now,
		messages: [],
		offers: [],
	};
	const message: ChatMessage = {
		id: newId("chm"),
		conversationId: conversation.id,
		authorRole: "buyer",
		authorUserId: mockUser.id,
		body,
		createdAt: now,
	};
	const next = {
		...conversation,
		lastMessageAt: now,
		updatedAt: now,
		messages: [...(conversation.messages ?? []), message],
	};
	buyerConversations.set(next.id, next);
	return next;
};

// ── Центр помощи (fd-support-01) ─────────────────────────────────────────────
// Публичный FAQ + обращения покупателя (in-memory тред с ответом поддержки).
const mockFaq: FaqList = {
	groups: [
		{
			category: "Заказы и доставка",
			articles: [
				{ id: "faq-1", category: "Заказы и доставка", question: "Как отследить заказ?", answer: "Статус заказа виден в разделе «Профиль» → «Заказы». Как только продавец передаёт посылку в доставку, там появляется трек-номер.", sortOrder: 0, published: true, createdAt: nowIso(), updatedAt: nowIso() },
				{ id: "faq-2", category: "Заказы и доставка", question: "Сколько идёт доставка?", answer: "Стандартная доставка занимает 3–7 дней, экспресс — 1–2 дня. Точные сроки продавец указывает при оформлении.", sortOrder: 1, published: true, createdAt: nowIso(), updatedAt: nowIso() },
			],
		},
		{
			category: "Оплата и возврат",
			articles: [
				{ id: "faq-3", category: "Оплата и возврат", question: "Какие способы оплаты доступны?", answer: "Оплата картой, внутренним балансом OSMIX или по счёту для юридических лиц.", sortOrder: 0, published: true, createdAt: nowIso(), updatedAt: nowIso() },
				{ id: "faq-4", category: "Оплата и возврат", question: "Как оформить возврат?", answer: "Откройте заказ и нажмите «Оформить возврат» в течение 14 дней после получения. Товары ручной работы под заказ возврату не подлежат.", sortOrder: 1, published: true, createdAt: nowIso(), updatedAt: nowIso() },
			],
		},
	],
};

type StoredTicket = Omit<SupportTicketDetail, "messageCount" | "lastMessageAt">;
const supportTickets = new Map<string, StoredTicket>();

const supportSummary = (ticket: StoredTicket): SupportTicket => {
	const { messages, ...rest } = ticket;
	return {
		...rest,
		messageCount: messages.length,
		lastMessageAt: messages[messages.length - 1]?.createdAt ?? ticket.updatedAt,
	};
};
const supportDetail = (ticket: StoredTicket): SupportTicketDetail => ({
	...supportSummary(ticket),
	messages: ticket.messages.map((m) => ({ ...m })),
});

// fd-analytics-05: Яндекс.Метрика — мок отдаёт демо-счётчик, чтобы витрина
// показала вставку тега. Боевой gateway вернёт counterId: null, если он не задан.
const mockMetrika: MetrikaConfig = { counterId: "99999999" };

// Цифровые UGC-артикулы (ТЗ 31.10.2026): реестр кодов и партнёрский кабинет в памяти.
const ugcMock = createUgcMock(
	() => published(),
	(shopId) => shopMeta.get(shopId)?.name ?? shopId,
	() => Boolean(currentBuyerToken && getBuyerAuthToken() === currentBuyerToken),
);

export const mock: Api = {
	cabinet: mockCabinet,
	ugc: ugcMock,
	// Акции на витрине (П3): горящие, календарь, подписка — ../promos.ts.
	promos: createPromosMock(() => published()),
	// Личные «Образы» покупателя (fd-discovery-04) — ../looks.ts.
	looks: createLooksMock((id) => published().find((p) => p.id === id)?.photos[0]),
	products: {
		list: async (query: ProductQuery): Promise<ProductPage> => {
			await sleep(120);
			let list = published();
			if (query.shopId) list = list.filter((p) => p.shopId === query.shopId);
			// Категория — вся ветка узла любой глубины, как у шлюза.
			if (query.categoryId) {
				const ids = mockSubtreeIds(query.categoryId);
				list = list.filter((p) => ids.has(p.categoryId));
			}
			if (query.categoryIds?.length) {
				const ids = new Set<string>();
				for (const cid of query.categoryIds) for (const id of mockSubtreeIds(cid)) ids.add(id);
				list = list.filter((p) => ids.has(p.categoryId));
			}
			if (query.brands?.length) list = list.filter((p) => query.brands!.includes(p.brand));
			if (query.colors?.length) list = list.filter((p) => query.colors!.includes(p.color));
			if (query.sizes?.length) list = list.filter((p) => p.sizes.some((s) => query.sizes!.includes(s)));
			if (query.priceMin != null) list = list.filter((p) => p.price >= query.priceMin!);
			if (query.priceMax != null) list = list.filter((p) => p.price <= query.priceMax!);
			if (query.inStock) list = list.filter((p) => p.stock > 0);
			// «Товар»: отмеченные типы по ИЛИ, как у гейтвея (productType).
			if (query.productTypes?.length && query.productTypes.length < 3) {
				const types = query.productTypes;
				list = list.filter(
					(p) =>
						(types.includes("in-stock") && p.stock > 0) ||
						(types.includes("customizable") && p.customization?.enabled === true) ||
						(types.includes("made-to-order") && (p.madeToOrder === true || p.customization?.enabled === true)),
				);
			}
			if (query.sale) list = list.filter((p) => p.oldPrice != null && p.oldPrice > p.price);
			if (query.search?.trim()) list = list.filter((p) => matchesSearch(p, query.search!.trim()));
			// fd-boutique-05: коллекция бутика — явные товары и товары её категорий.
			if (query.collectionId && query.shopId) {
				const shop = mockShops.find((x) => x.id === query.shopId);
				const collection = shop
					? buildStorefront(shop).config.collections.items.find((c) => c.id === query.collectionId)
					: undefined;
				const ids = new Set(collection?.productIds ?? []);
				const cats = new Set(collection?.categoryIds ?? []);
				list = list.filter((p) => ids.has(p.id) || cats.has(p.categoryId));
			}

			const sort = query.sort ?? "relevance";
			list = [...list].sort((a, b) => {
				if (sort === "price-asc") return a.price - b.price;
				if (sort === "price-desc") return b.price - a.price;
				if (sort === "newest") return a.createdAt < b.createdAt ? 1 : -1;
				return a.updatedAt < b.updatedAt ? 1 : -1;
			});

			const page = Math.max(1, query.page ?? 1);
			const pageSize = Math.min(60, Math.max(1, query.pageSize ?? 24));
			const total = list.length;
			const start = (page - 1) * pageSize;
			// Код из 8 цифр, совпавший с активным артикулом, - как у шлюза: поле ugcRedirect.
			const ugc = query.search && looksLikeUgcArticle(query.search) ? ugcMock.resolveSync(query.search) : null;
			return {
				data: list.slice(start, start + pageSize),
				meta: { page, pageSize, total, totalPages: Math.max(1, Math.ceil(total / pageSize)) },
				...(ugc?.found ? { ugcRedirect: ugc } : {}),
			};
		},
		get: async (id: string): Promise<Product> => {
			await sleep(80);
			const p = byId(id);
			if (!p) throw new Error("Product not found");
			return p;
		},
		byIds: async (ids: string[]): Promise<Product[]> => {
			await sleep(80);
			return ids.map(byId).filter((p): p is Product => Boolean(p));
		},
		facets: async (query): Promise<ProductFacets> => {
			await sleep(60);
			let list = published();
			if (query.shopId) list = list.filter((p) => p.shopId === query.shopId);
			if (query.search?.trim()) list = list.filter((p) => matchesSearch(p, query.search!.trim()));
			// Категория сужает оси так же, как гейтвей: сама категория плюс её
			// подкатегории (у обуви — размеры обуви, а не одежды).
			if (query.categoryId) {
				const ids = mockSubtreeIds(query.categoryId);
				list = list.filter((p) => ids.has(p.categoryId));
			}
			const prices = list.map((p) => p.price);
			return {
				categories: categoryFacets(list),
				brands: counts(list.map((p) => p.brand)),
				colors: counts(list.map((p) => p.color)),
				sizes: groupSizeFacet(counts(list.flatMap((p) => p.sizes))).flatMap((g) => g.values),
				// Мок повторяет раскладку гейтвея по шкалам: офлайн-режим обязан
				// показывать тот же фильтр, что и с бэкендом.
				sizeGroups: groupSizeFacet(counts(list.flatMap((p) => p.sizes))),
				price: { min: prices.length ? Math.min(...prices) : 0, max: prices.length ? Math.max(...prices) : 0 },
			};
		},
		reviews: async (id: string, query: ProductReviewsQuery = {}): Promise<ProductReviewList> => {
			await sleep(100);
			// Сводка считается по всем отзывам товара; фильтры сужают только ленту.
			const all = productReviewsStore.get(id) ?? [];
			let items = [...all];
			if (query.rating != null) items = items.filter((r) => r.rating === query.rating);
			if (query.withPhotos) items = items.filter((r) => r.photoUrls.length > 0);
			if (query.variant) items = items.filter((r) => r.variant === query.variant);
			const sort: ProductReviewSort = query.sort ?? "newest";
			items.sort((a, b) =>
				sort === "rating" ? b.rating - a.rating : b.createdAt.localeCompare(a.createdAt),
			);
			return { productId: id, summary: summarizeReviews(all), items };
		},
		questions: async (id: string) => {
			await sleep(80);
			return { productId: id, items: [...(productQuestionsStore.get(id) ?? [])] };
		},
	},

	categories: {
		list: async () => {
			await sleep(50);
			return mockCategories;
		},
		archived: async () => {
			await sleep(30);
			return mockArchivedCategories;
		},
	},

	shops: {
		list: async (): Promise<Shop[]> => {
			await sleep(80);
			return mockShops.map((s) => ({
				...s,
				productCount: published().filter((p) => p.shopId === s.id).length,
			}));
		},
		get: async (slug: string): Promise<Shop> => {
			await sleep(60);
			const s = mockShops.find((x) => x.slug === slug);
			if (!s) throw new Error("Shop not found");
			return { ...s, productCount: published().filter((p) => p.shopId === s.id).length };
		},
		storefront: async (slug: string): Promise<Storefront> => {
			await sleep(120);
			const s = mockShops.find((x) => x.slug === slug);
			if (!s) throw new Error("Shop not found");
			return buildStorefront(s);
		},
		createCustomOrder: async (slug, raw) => {
			await sleep(150);
			requireBuyer();
			const s = mockShops.find((x) => x.slug === slug);
			if (!s) throw new Error("Shop not found");
			const config = buildStorefront(s).config;
			const input = CreateShopCustomOrderSchema.parse(raw);
			const at = nowIso();
			const conversationId = newId("chat");
			buyerConversations.set(conversationId, {
				id: conversationId,
				shopId: s.id,
				shopName: s.name,
				buyerUserId: mockUser.id,
				subject: `Индивидуальный заказ: ${input.productType}`,
				context: { kind: "custom_order" },
				lastMessageAt: at,
				lastMessagePreview: input.description,
				unreadCount: 0,
				createdAt: at,
				updatedAt: at,
				messages: [
					{ id: newId("chm"), conversationId, authorRole: "buyer", authorUserId: mockUser.id, body: input.description, imageUris: input.photos, attachments: [], createdAt: at },
				],
			});
			const order: ShopCustomOrder = {
				id: newId("cord"),
				shopId: s.id,
				shopSlug: s.slug,
				shopName: s.name,
				conversationId,
				productType: input.productType,
				description: input.description,
				quantity: input.quantity,
				size: input.size,
				parameters: input.parameters,
				photos: input.photos,
				budget: input.budget,
				deadline: input.deadline,
				paymentType: input.paymentType,
				contactName: input.contactName,
				contactPhone: input.contactPhone,
				contactMethod: input.contactMethod,
				estimatePrice: estimateShopCustomOrderPrice(config.customOrder, input),
				status: "sent",
				createdAt: at,
				updatedAt: at,
			};
			buyerCustomOrders.set(order.id, order);
			return order;
		},
	},

	cart: {
		create: async (): Promise<Cart> => {
			const cart: StoredCart = { id: newId("cart"), items: [], updatedAt: new Date().toISOString() };
			carts.set(cart.id, cart);
			return hydrate(cart);
		},
		get: async (id: string) => {
			await sleep(60);
			return hydrate(mustCart(id));
		},
		addItem: async (id: string, req: AddCartItemRequest) => {
			const cart = mustCart(id);
			const p = byId(req.productId);
			if (!p) throw new Error("Product not found");
			if (p.status !== "published") throw new Error("Product is not available");
			const qty = Math.max(1, Math.floor(req.qty || 1));
			const variants = p.variants ?? [];
			if (variants.length && !req.variantId) throw new Error("variantId is required for products with variants");
			const variant = req.variantId ? variants.find((v) => v.id === req.variantId) : undefined;
			if (req.variantId && !variant) throw new Error("Variant is not available");
			const size = variant?.size ?? req.size ?? p.sizes[0] ?? "Универсальный";
			const color = variant?.color ?? req.color ?? p.color;
			const stock = variant?.stock ?? p.stock;
			const existing = variant
				? cart.items.find((i) => i.productId === req.productId && i.variantId === variant.id)
				: cart.items.find((i) => i.productId === req.productId && !i.variantId && i.size === size && i.color === color);
			const nextQty = (existing?.qty ?? 0) + qty;
			if (nextQty > stock) throw new Error("Not enough stock");
			if (existing) existing.qty = nextQty;
			else cart.items.push({ id: newId("ci"), productId: req.productId, variantId: variant?.id, size, color, qty });
			cart.updatedAt = new Date().toISOString();
			return hydrate(cart);
		},
		updateItem: async (id: string, itemId: string, qty: number) => {
			const cart = mustCart(id);
			const item = cart.items.find((i) => i.id === itemId);
			if (!item) throw new Error("Item not found");
			if (qty <= 0) cart.items = cart.items.filter((i) => i.id !== itemId);
			else {
				const p = byId(item.productId);
				const variant = item.variantId ? p?.variants?.find((v) => v.id === item.variantId) : undefined;
				const stock = variant?.stock ?? p?.stock;
				if (stock != null && qty > stock) throw new Error("Not enough stock");
				item.qty = qty;
			}
			cart.updatedAt = new Date().toISOString();
			return hydrate(cart);
		},
		removeItem: async (id: string, itemId: string) => {
			const cart = mustCart(id);
			cart.items = cart.items.filter((i) => i.id !== itemId);
			cart.updatedAt = new Date().toISOString();
			return hydrate(cart);
		},
		removeItems: async (id: string, itemIds: string[]) => {
			const cart = mustCart(id);
			const drop = new Set(itemIds);
			cart.items = cart.items.filter((i) => !drop.has(i.id));
			cart.updatedAt = new Date().toISOString();
			return hydrate(cart);
		},
		applyPromo: async (id: string, code: string) => {
			const cart = mustCart(id);
			const normalized = code.trim().toUpperCase();
			if (!MOCK_PROMOS[normalized]) throw new Error("Промокод не найден или неактивен");
			cart.promoCode = normalized;
			cart.updatedAt = new Date().toISOString();
			return hydrate(cart);
		},
		clearPromo: async (id: string) => {
			const cart = mustCart(id);
			cart.promoCode = undefined;
			cart.updatedAt = new Date().toISOString();
			return hydrate(cart);
		},
		// В моках ленты промокодов нет: карточки собирает только шлюз.
		promoFeed: async () => ({ items: [] }),
	},

	orders: {
		// Create a pending order — authoritative total, no stock/cart mutation
		// (mirrors the gateway). The offline path has no widget; confirm finalizes.
		createPending: async (req: CreateOrderRequest): Promise<Order> => {
			await sleep(300);
			const hydrated = pickLines(hydrate(mustCart(req.cartId)), req.lineIds);
			if (hydrated.items.length === 0) throw new Error("Cart is empty");
			for (const i of hydrated.items) if (!i.available) throw new Error(`«${i.name}» больше недоступен`);

			const byShop = new Map<string, CartItem[]>();
			for (const i of hydrated.items) {
				const arr = byShop.get(i.shopId) ?? [];
				arr.push(i);
				byShop.set(i.shopId, arr);
			}
			// Re-price each group's chosen selection from the flat config (the mock
			// never trusts the client's number either — mirrors the gateway).
			const bySel = new Map((req.selections ?? []).map((s) => [s.shopId, s]));
			const groups: OrderGroup[] = [...byShop.entries()].map(([shopId, items]) => {
				const subtotal = items.reduce((s, i) => s + i.lineTotal, 0);
				const shop = shopMeta.get(shopId);
				const sel = bySel.get(shopId);
				const method = sel?.method ?? "standard";
				const deliveryFee = subtotal >= deliveryConfig.freeOver ? 0 : deliveryConfig.fees[method];
				return {
					shopId,
					shopName: shop?.name ?? shopId,
					shopSlug: shop?.slug ?? shopId,
					items: items.map((i) => ({
						productId: i.productId,
						name: i.name,
						brand: i.brand,
						photo: i.photo,
						variantId: i.variantId,
						variantLabel: i.variantLabel,
						options: i.options,
						size: i.size,
						color: i.color,
						qty: i.qty,
						unitPrice: i.unitPrice,
						oldUnitPrice: i.oldUnitPrice,
						lineTotal: i.lineTotal,
						weightGrams: i.weightGrams,
						lengthCm: i.lengthCm,
						widthCm: i.widthCm,
						heightCm: i.heightCm,
					})),
					subtotal,
					deliveryFee,
					shipment: sel
						? {
								carrier: sel.carrier,
								method: sel.method,
								tariffCode: sel.tariffCode,
								tariffName: sel.tariffName,
								deliveryDays: sel.deliveryDays,
								toPickupPoint: sel.toPickupPoint,
								pvzCode: sel.pvzCode,
								pvzAddress: sel.pvzAddress,
							}
						: undefined,
				};
			});
			const subtotal = groups.reduce((s, g) => s + g.subtotal, 0);
			const deliveryTotal = groups.reduce((s, g) => s + g.deliveryFee, 0);
			// «Скидка OSMIX» из корзины вычитается из суммы к оплате (как в gateway).
			const discount = Math.min(round2(hydrated.discount ?? 0), subtotal);
			const order: Order = {
				id: newId("ord"),
				status: "pending",
				contact: req.contact,
				address: req.address,
				delivery: deriveMethod(groups),
				payment: { provider: "mock", status: "pending", brand: "", last4: "" },
				groups,
				subtotal,
				discount,
				deliveryTotal,
				total: round2(subtotal - discount + deliveryTotal),
				itemCount: hydrated.items.reduce((s, i) => s + i.qty, 0),
				createdAt: new Date().toISOString(),
			};
			const isBuyer = Boolean(currentBuyerToken && getBuyerAuthToken() === currentBuyerToken);

			// fd-payment-02: оплата по счёту для ЮЛ. Заказ остаётся pending с
			// пометкой invoice; ни баланс, ни провайдер не задействованы.
			if (req.paymentMethod === "invoice") {
				if (!isBuyer) throw new Error("Оплата по счёту доступна только авторизованному покупателю");
				order.paymentMethod = "invoice";
				order.payableTotal = order.total;
			} else if (req.useBalance || req.balanceAmount != null) {
				// fd-payment-03: списание (частично/полностью) с внутреннего баланса.
				if (!isBuyer) throw new Error("Оплата балансом доступна только авторизованному покупателю");
				const requested = req.balanceAmount != null ? round2(req.balanceAmount) : order.total;
				if (requested < 0) throw new Error("Сумма списания с баланса не может быть отрицательной");
				const applied = round2(Math.min(requested, buyerBalanceAmount, order.total));
				if (applied > 0) {
					order.balanceApplied = applied;
					pushLedger(-applied, "spend", `Оплата заказа ${order.id}`, { refType: "order", refId: order.id });
				}
				order.payableTotal = round2(order.total - applied);
			}
			orders.set(order.id, order);
			if (currentBuyerToken && getBuyerAuthToken() === currentBuyerToken) buyerOrderIds.add(order.id);
			orderCarts.set(order.id, req.cartId);
			return order;
		},
		// Finalize the offline order: approve (decrement stock, drop cart),
		// idempotent on an already-finalized order. No real PSP, no widget.
		confirm: async (id: string): Promise<Order> => {
			await sleep(300);
			const o = orders.get(id);
			if (!o) throw new Error("Order not found");
			if (o.status !== "pending") return o;
			for (const g of o.groups) {
				for (const it of g.items) {
					const p = byId(it.productId);
					if (!p) continue;
					const variant = it.variantId ? p.variants?.find((v) => v.id === it.variantId) : undefined;
					if (variant) {
						variant.stock = Math.max(0, variant.stock - it.qty);
						p.stock = p.variants?.reduce((sum, v) => sum + v.stock, 0) ?? p.stock;
					} else {
						p.stock = Math.max(0, p.stock - it.qty);
					}
				}
			}
			const paid: Order = { ...o, status: "new", payment: { provider: "mock", status: "approved", transactionId: `mock_${id}`, brand: "Visa", last4: "4242" } };
			orders.set(id, paid);
			if (buyerOrderIds.has(id)) ensureMockPurchaseReceipt(paid);
			const cartId = orderCarts.get(id);
			if (cartId) carts.delete(cartId);
			return paid;
		},
		// fd-payment-01: офлайн-оплата сохранённой картой проводит заказ сразу —
		// как mock-провайдер шлюза. Платить можно только картой с токеном.
		paySavedCard: async (id: string, paymentMethodId: string) => {
			const method = buyerPaymentMethods.get(paymentMethodId);
			if (!method?.chargeable) throw new Error("Эта карта сохранена не через платёжную систему");
			const order = await mock.orders.confirm(id);
			return { status: "paid" as const, order };
		},
		get: async (id: string): Promise<Order> => {
			await sleep(60);
			const o = orders.get(id);
			if (!o) throw new Error("Order not found");
			return o;
		},
		// The mock has no live carrier, so flat-rate orders never produce a
		// trackable shipment record.
		shipments: async () => {
			await sleep(40);
			return [];
		},
	},

	payments: {
		config: async () => {
			await sleep(20);
			// fd-payment-04: ставка комиссии площадки для показа (12% — как дефолт
			// в gateway). Не влияет на суммы заказа — комиссию несёт продавец.
			return { provider: "mock" as const, currency: "RUB", commissionRate: 0.12, savedCards: true };
		},
		// Офлайн-режим платить негде: редирект-ссылки не существует, и
		// обещать её мок не вправе.
		url: async (): Promise<{ url: string }> => {
			await sleep(20);
			throw new Error("Офлайн-режим: оплата по ссылке недоступна");
		},
	},

	settings: {
		delivery: async (): Promise<DeliveryConfig> => {
			await sleep(40);
			return deliveryConfig;
		},
	},

	delivery: {
		// Offline quote: flat options per shop group (the mock has no carrier).
		quote: async (req) => {
			await sleep(120);
			const hydrated = pickLines(hydrate(mustCart(req.cartId)), req.lineIds);
			const byShop = new Map<string, number>();
			for (const i of hydrated.items) byShop.set(i.shopId, (byShop.get(i.shopId) ?? 0) + i.lineTotal);
			return {
				groups: [...byShop.entries()].map(([shopId, subtotal]) => ({
					shopId,
					shopName: shopMeta.get(shopId)?.name ?? shopId,
					carrier: "flat" as const,
					options: flatOptions(subtotal),
				})),
			};
		},
		// No carrier offline → no pickup points.
		points: async () => {
			await sleep(40);
			return [];
		},
		// Офлайн: плоские тарифы площадки, своей доставки продавца нет.
		productOptions: async (req) => {
			await sleep(60);
			const product = mockProducts.find((p) => p.id === req.productId);
			const shopId = product?.shopId ?? "";
			return {
				shopId,
				shopName: shopMeta.get(shopId)?.name ?? shopId,
				carrier: "flat" as const,
				options: flatOptions(product?.price ?? 0),
				city: req.city,
				self: null,
			};
		},
		// Офлайн своей доставки продавцов нет — пустое предложение по группам.
		selfOptions: async (req) => {
			await sleep(40);
			const hydrated = pickLines(hydrate(mustCart(req.cartId)), req.lineIds);
			const shops = [...new Set(hydrated.items.map((i) => i.shopId))];
			return shops.map((shopId) => ({ shopId, shopName: shopMeta.get(shopId)?.name ?? shopId, offer: null }));
		},
		// Офлайн самовывоза от продавцов нет — пустое предложение по группам.
		sellerPickupOptions: async (req) => {
			await sleep(40);
			const hydrated = pickLines(hydrate(mustCart(req.cartId)), req.lineIds);
			const shops = [...new Set(hydrated.items.map((i) => i.shopId))];
			return shops.map((shopId) => ({ shopId, shopName: shopMeta.get(shopId)?.name ?? shopId, offer: null }));
		},
	},

	geo: {
		countries: async () => {
			await sleep(40);
			return mockCountries;
		},
		currencies: async () => {
			await sleep(40);
			return mockCurrencies;
		},
		// Мок обратного геокода: демонстрирует автоопределение города (реальный
		// бэк отдаёт город по координатам через DaData).
		reverse: async (_lat: number, _lon: number) => {
			await sleep(60);
			return { city: "Санкт-Петербург", region: "Санкт-Петербург", country: "Россия" };
		},
		addressSuggest: async (query: string, city?: string) => {
			await sleep(120);
			return suggestMockAddresses(query, city);
		},
		citySuggest: async (query: string) => {
			await sleep(120);
			return suggestMockCities(query);
		},
	},

	// Телеметрия витрины. В офлайн-режиме её некуда складывать, и копить её в
	// памяти вкладки незачем — никто эти события не читает. Молчаливый no-op
	// держит витрину работающей ровно так же, как с живым шлюзом.
	discovery: {
		recordEvents: async () => {},
		recordOrderEvent: async () => {},
	},

	feed: {
		blocks: async (segment, department) => {
			await sleep(120);
			return buildFeedBlocks(segment ?? "womens", department ?? "clothes");
		},
		collections: async () => {
			await sleep(120);
			return mockFeedCollections;
		},
		collection: async (slug: string) => {
			await sleep(80);
			const c = mockFeedCollections.find((x) => x.slug === slug);
			if (!c) throw new Error("Collection not found");
			return c;
		},
		imageFile: async (url: string) => {
			const res = await fetch(url, { mode: "cors", credentials: "omit" });
			if (!res.ok) throw new Error(`Image request failed: ${res.status}`);
			return res.blob();
		},
		productCollections: async (productId: string) => {
			await sleep(80);
			return mockFeedCollections
				.filter((collection) => collection.productIds.includes(productId))
				.slice(0, 8)
				.map((collection) => ({
					id: collection.id,
					slug: collection.slug,
					title: collection.title,
					description: collection.description,
					type: collection.type,
					cover: collection.cover,
					author: collection.author,
					productCount: collection.productCount,
					productIds: collection.productIds,
					source: "editorial" as const,
				}));
		},
		// Офлайн-мок персональной ленты: обычный порядок каталога, курсор = смещение.
		forYou: async ({ seed, cursor, clientId: _clientId, ...query }) => {
			const pageSize = query.pageSize ?? 24;
			const offset = cursor ? Number(cursor) || 0 : 0;
			const page = await mock.products.list({ ...query, page: Math.floor(offset / pageSize) + 1, pageSize });
			const next = offset + page.data.length;
			return {
				data: page.data,
				meta: { ...page.meta, nextCursor: next < page.meta.total ? String(next) : null, seed, personalized: false },
			};
		},
	},
	// Партнёрская ссылка в офлайн-моке: считать нечего — воронку ведёт шлюз.
	referral: {
		visit: async () => {},
		attribute: async () => ({ attributed: false }),
	},
	// Ссылка магазина в офлайн-моке: магазинов по коду нет - ведём на главную.
	shopInvite: {
		resolve: async () => null,
		visit: async () => {},
		bind: async () => ({ bound: false, reason: "invalid_code" as const }),
	},
	accountDeletion: {
		check: async () => ({ canDelete: true, blockers: [] }),
		start: async () => ({ sessionId: "mock_del", maskedTarget: "+7 *** ***-**-33", expiresAt: new Date(Date.now() + 300_000).toISOString() }),
		confirm: async (_sessionId, code) => (code === "111111" ? { deleted: true as const } : { deleted: false as const, attemptsLeft: 2 }),
	},
	// Мок без внешних провайдеров - как шлюз без ключей: кнопок входа нет.
	oauth: {
		providers: async () => [],
		start: async () => {
			throw new Error("Вход через этот сервис не подключён");
		},
		exchange: async () => {
			throw new Error("Ссылка входа устарела. Попробуйте ещё раз");
		},
		claim: async () => [],
		identities: async () => [],
		unlink: async () => {},
	},
	buyer: {
		startAuth: async ({ phone, deviceName }) => {
			await sleep(180);
			currentAuthSession = {
				sessionId: newId("sms"),
				maskedTarget: phone.replace(/\d(?=\d{2})/g, "•"),
				expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(),
			};
			if (deviceName) currentAuthSession = { ...currentAuthSession };
			return currentAuthSession;
		},
		verifyAuth: async ({ sessionId, code, deviceName }): Promise<BuyerAuthVerifyResponse> => {
			await sleep(180);
			if (!currentAuthSession || currentAuthSession.sessionId !== sessionId || code !== "111111") {
				return { success: false, attemptsLeft: 2 };
			}
			currentBuyerToken = `mock_buyer_${Math.random().toString(36).slice(2)}`;
			const session: BuyerSession = {
				id: newId("session"),
				deviceName: deviceName || "Mock browser",
				createdAt: nowIso(),
				lastSeenAt: nowIso(),
				current: true,
			};
			buyerSessions.clear();
			buyerSessions.set(session.id, session);
			return { success: true, token: currentBuyerToken, user: mockUser, profile: mockProfile };
		},
		logout: async () => {
			await sleep(80);
			currentBuyerToken = null;
		},
		me: async () => {
			await sleep(80);
			return requireBuyer();
		},
		updateProfile: async (input: BuyerProfilePatch) => {
			await sleep(120);
			requireBuyer();
			mockProfile = { ...mockProfile, ...input, updatedAt: nowIso() };
			return requireBuyer();
		},
		updateProfileExt: async (input) => {
			await sleep(120);
			requireBuyer();
			const fullName = [input.firstName ?? mockProfile.firstName, input.lastName ?? mockProfile.lastName].filter(Boolean).join(" ");
			mockProfile = { ...mockProfile, ...input, name: input.name ?? (fullName || mockProfile.name), updatedAt: nowIso() };
			return requireBuyer();
		},
		uploadAvatar: async (file) => {
			await sleep(150);
			requireBuyer();
			const avatarUrl = URL.createObjectURL(file);
			mockProfile = { ...mockProfile, avatarUrl, updatedAt: nowIso() };
			return { avatarUrl };
		},
		deleteAvatar: async () => {
			await sleep(80);
			requireBuyer();
			mockProfile = { ...mockProfile, avatarUrl: undefined, updatedAt: nowIso() };
		},
		startPhoneChange: async () => {
			await sleep(100);
			requireBuyer();
			return { sessionId: newId("phc"), maskedTarget: mockProfile.phone.replace(/\d(?=\d{4})/g, "*"), expiresAt: new Date(Date.now() + 600_000).toISOString() };
		},
		verifyPhoneChangeCurrent: async (_sessionId, code) => {
			await sleep(100);
			requireBuyer();
			return { success: code === "111111", attemptsLeft: code === "111111" ? 5 : 4 };
		},
		setPhoneChangeNew: async (_sessionId, phone) => {
			await sleep(100);
			requireBuyer();
			mockPendingPhone = phone;
			return { maskedTarget: phone.replace(/\d(?=\d{4})/g, "*"), expiresAt: new Date(Date.now() + 600_000).toISOString() };
		},
		verifyPhoneChangeNew: async (_sessionId, code) => {
			await sleep(100);
			requireBuyer();
			if (code !== "111111" || !mockPendingPhone) throw new Error("Неверный код");
			mockProfile = { ...mockProfile, phone: mockPendingPhone, updatedAt: nowIso() };
			mockPendingPhone = null;
			return requireBuyer();
		},
		listSessions: async () => {
			await sleep(80);
			requireBuyer();
			return [...buyerSessions.values()].map((s, index) => ({ ...s, current: index === 0 }));
		},
		revokeSession: async (id: string) => {
			await sleep(80);
			requireBuyer();
			const session = buyerSessions.get(id);
			if (!session) throw new Error("Session not found");
			buyerSessions.delete(id);
			if (session.current) currentBuyerToken = null;
		},
		revokeOtherSessions: async () => {
			await sleep(80);
			requireBuyer();
			for (const [id, session] of buyerSessions) if (!session.current) buyerSessions.delete(id);
		},
		listAddresses: async () => {
			await sleep(80);
			requireBuyer();
			return [...buyerAddresses.values()].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
		},
		createAddress: async (input: BuyerAddressInput) => {
			await sleep(120);
			requireBuyer();
			const isDefault = input.isDefault || buyerAddresses.size === 0;
			if (isDefault) for (const [id, a] of buyerAddresses) buyerAddresses.set(id, { ...a, isDefault: false });
			const address: BuyerAddress = { ...input, isDefault, id: newId("adr"), createdAt: nowIso(), updatedAt: nowIso() };
			buyerAddresses.set(address.id, address);
			return address;
		},
		updateAddress: async (id: string, input: BuyerAddressInput) => {
			await sleep(120);
			requireBuyer();
			if (!buyerAddresses.has(id)) throw new Error("Address not found");
			if (input.isDefault) for (const [otherId, a] of buyerAddresses) buyerAddresses.set(otherId, { ...a, isDefault: false });
			const address: BuyerAddress = { ...input, id, createdAt: buyerAddresses.get(id)!.createdAt, updatedAt: nowIso() };
			buyerAddresses.set(id, address);
			return address;
		},
		deleteAddress: async (id: string) => {
			await sleep(80);
			requireBuyer();
			if (!buyerAddresses.delete(id)) throw new Error("Address not found");
		},
		listRecipients: async () => {
			await sleep(80);
			requireBuyer();
			return [...buyerRecipients.values()].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
		},
		createRecipient: async (input) => {
			await sleep(100);
			requireBuyer();
			const isDefault = Boolean(input.isDefault) || buyerRecipients.size === 0;
			if (isDefault) for (const [id, r] of buyerRecipients) buyerRecipients.set(id, { ...r, isDefault: false });
			const recipient: BuyerRecipient = {
				id: newId("rcp"),
				firstName: input.firstName,
				lastName: input.lastName ?? "",
				phone: input.phone,
				isDefault,
				createdAt: nowIso(),
				updatedAt: nowIso(),
			};
			buyerRecipients.set(recipient.id, recipient);
			return recipient;
		},
		updateRecipient: async (id, input) => {
			await sleep(100);
			requireBuyer();
			const current = buyerRecipients.get(id);
			if (!current) throw new Error("Recipient not found");
			if (input.isDefault) for (const [otherId, r] of buyerRecipients) buyerRecipients.set(otherId, { ...r, isDefault: false });
			const recipient: BuyerRecipient = {
				...current,
				firstName: input.firstName,
				lastName: input.lastName ?? "",
				phone: input.phone,
				isDefault: Boolean(input.isDefault) || current.isDefault,
				updatedAt: nowIso(),
			};
			buyerRecipients.set(id, recipient);
			return recipient;
		},
		deleteRecipient: async (id) => {
			await sleep(80);
			requireBuyer();
			if (!buyerRecipients.delete(id)) throw new Error("Recipient not found");
		},
		listPickupPoints: async () => {
			await sleep(80);
			requireBuyer();
			return [...buyerPickupPoints.values()].sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt));
		},
		savePickupPoint: async (input) => {
			await sleep(80);
			requireBuyer();
			const point: SavedPickupPoint = {
				name: "",
				city: "",
				workTime: "",
				isDressingRoom: false,
				...input,
				lastUsedAt: nowIso(),
			};
			buyerPickupPoints.set(`${input.carrier}:${input.code}`, point);
			return point;
		},
		deletePickupPoint: async (carrier, code) => {
			await sleep(60);
			requireBuyer();
			if (!buyerPickupPoints.delete(`${carrier}:${code}`)) throw new Error("Pickup point not found");
		},
		suggestCompany: async (query) => {
			await sleep(150);
			const digits = query.replace(/\D/g, "");
			if (digits.length < 10) return [];
			return [
				{
					inn: digits,
					shortName: "ООО «Пример»",
					fullName: "Общество с ограниченной ответственностью «Пример»",
					legalAddress: "г. Москва, ул. Тверская, д. 1",
					kpp: "770101001",
					ogrn: "1027700000000",
				},
			];
		},
		listPaymentMethods: async () => {
			await sleep(80);
			requireBuyer();
			return [...buyerPaymentMethods.values()].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
		},
		createPaymentMethod: async (input: BuyerPaymentMethodInput) => {
			await sleep(120);
			requireBuyer();
			const isDefault = input.isDefault || buyerPaymentMethods.size === 0;
			if (isDefault) for (const [id, p] of buyerPaymentMethods) buyerPaymentMethods.set(id, { ...p, isDefault: false });
			const method: BuyerPaymentMethod = { ...input, isDefault, id: newId("pay"), createdAt: nowIso(), updatedAt: nowIso() };
			buyerPaymentMethods.set(method.id, method);
			return method;
		},
		updatePaymentMethod: async (id: string, patch: BuyerPaymentMethodPatch) => {
			await sleep(100);
			requireBuyer();
			const existing = buyerPaymentMethods.get(id);
			if (!existing) throw new Error("Payment method not found");
			if (patch.isDefault) for (const [otherId, p] of buyerPaymentMethods) buyerPaymentMethods.set(otherId, { ...p, isDefault: false });
			const updated: BuyerPaymentMethod = {
				...existing,
				label: patch.label ?? existing.label,
				isDefault: patch.isDefault ?? existing.isDefault,
				updatedAt: nowIso(),
			};
			buyerPaymentMethods.set(id, updated);
			return updated;
		},
		deletePaymentMethod: async (id: string) => {
			await sleep(80);
			requireBuyer();
			if (!buyerPaymentMethods.delete(id)) throw new Error("Payment method not found");
		},
		listSbpBanks: async () => {
			await sleep(60);
			return MOCK_SBP_BANKS;
		},
		createSbpPaymentMethod: async (input) => {
			await sleep(120);
			requireBuyer();
			const bank = MOCK_SBP_BANKS.find((b) => b.id === input.bankId);
			if (!bank) throw new Error("Unknown SBP bank");
			const method: BuyerPaymentMethod = { provider: "sbp", providerToken: bank.id, brand: bank.name, last4: "", label: input.label ?? bank.name, expiresAt: "", isDefault: buyerPaymentMethods.size === 0, id: newId("pay"), createdAt: nowIso(), updatedAt: nowIso() };
			buyerPaymentMethods.set(method.id, method);
			return method;
		},
		createCompanyPaymentMethod: async (input) => {
			await sleep(120);
			requireBuyer();
			if (!/^\d{10}(\d{2})?$/.test(input.inn.trim())) throw new Error("Invalid INN");
			const method: BuyerPaymentMethod = { provider: "company", providerToken: input.inn.trim(), brand: "", last4: "", label: input.label ?? `ЮЛ ${input.inn.trim()}`, expiresAt: "", isDefault: buyerPaymentMethods.size === 0, id: newId("pay"), createdAt: nowIso(), updatedAt: nowIso() };
			buyerPaymentMethods.set(method.id, method);
			return method;
		},
		getBalance: async (): Promise<BuyerBalance> => {
			await sleep(80);
			requireBuyer();
			return { userId: mockUser.id, amount: buyerBalanceAmount, currency: BALANCE_CURRENCY, updatedAt: buyerBalanceUpdatedAt };
		},
		listLedger: async (): Promise<LedgerEntry[]> => {
			await sleep(80);
			requireBuyer();
			return buyerLedger.map((e) => ({ ...e }));
		},
		topupBalance: async (amount: number): Promise<BalanceTopup> => {
			await sleep(120);
			requireBuyer();
			const value = round2(amount);
			if (!Number.isFinite(value) || value <= 0) throw new Error("Сумма пополнения должна быть больше нуля");
			const topup: BalanceTopup = {
				id: newId("top"),
				userId: mockUser.id,
				amount: value,
				currency: BALANCE_CURRENCY,
				status: "pending",
				provider: "mock",
				createdAt: nowIso(),
				updatedAt: nowIso(),
			};
			buyerTopups.set(topup.id, topup);
			return topup;
		},
		topupPaymentUrl: async (): Promise<{ url: string }> => {
			await sleep(20);
			throw new Error("Офлайн-режим: оплата по ссылке недоступна");
		},
		confirmTopup: async (id: string): Promise<TopupConfirmResult> => {
			await sleep(200);
			requireBuyer();
			const topup = buyerTopups.get(id);
			if (!topup) throw new Error("Пополнение не найдено");
			if (topup.status !== "paid") {
				const paid: BalanceTopup = { ...topup, status: "paid", transactionId: `mock_${id}`, updatedAt: nowIso() };
				buyerTopups.set(id, paid);
				pushLedger(paid.amount, "topup", "Пополнение баланса", { refType: "topup", refId: id });
			}
			const settled = buyerTopups.get(id)!;
			return {
				topup: settled,
				balance: { userId: mockUser.id, amount: buyerBalanceAmount, currency: BALANCE_CURRENCY, updatedAt: buyerBalanceUpdatedAt },
			};
		},
		getBillingProfile: async (): Promise<BillingProfile | null> => {
			await sleep(80);
			requireBuyer();
			return buyerBillingProfile ? { ...buyerBillingProfile } : null;
		},
		updateBillingProfile: async (input: BillingProfileInput): Promise<BillingProfile> => {
			await sleep(140);
			requireBuyer();
			if (!input.companyName?.trim() || !input.inn?.trim()) throw new Error("Укажите название организации и ИНН");
			buyerBillingProfile = {
				userId: mockUser.id,
				companyName: input.companyName.trim(),
				inn: input.inn.trim(),
				kpp: input.kpp?.trim() ?? "",
				ogrn: input.ogrn?.trim() ?? "",
				legalAddress: input.legalAddress?.trim() ?? "",
				bankName: input.bankName?.trim() ?? "",
				bankAccount: input.bankAccount?.trim() ?? "",
				corrAccount: input.corrAccount?.trim() ?? "",
				bik: input.bik?.trim() ?? "",
				contactName: input.contactName?.trim() ?? "",
				contactEmail: input.contactEmail?.trim() ?? "",
				contactPhone: input.contactPhone?.trim() ?? "",
				createdAt: buyerBillingProfile?.createdAt ?? nowIso(),
				updatedAt: nowIso(),
			};
			return { ...buyerBillingProfile };
		},
		getOrderInvoice: async (id: string): Promise<Invoice> => {
			await sleep(120);
			requireBuyer();
			const order = orders.get(id);
			if (!order) throw new Error("Order not found");
			if (order.paymentMethod !== "invoice") throw new Error("Счёт доступен только для заказов с оплатой по счёту");
			if (!buyerBillingProfile) throw new Error("Заполните реквизиты ЮЛ в кабинете перед получением счёта");
			return {
				orderId: order.id,
				number: order.id.replace(/^ord_/, "INV-"),
				issuedAt: order.createdAt,
				currency: BALANCE_CURRENCY,
				buyer: { ...buyerBillingProfile },
				platform: { ...mockPlatformRequisites },
				items: order.groups.flatMap((g) =>
					g.items.map((i) => ({ name: i.name, shopName: g.shopName, qty: i.qty, unitPrice: i.unitPrice, lineTotal: i.lineTotal })),
				),
				subtotal: order.subtotal,
				deliveryTotal: order.deliveryTotal,
				total: order.total,
			};
		},
		listOrders: async () => {
			await sleep(80);
			requireBuyer();
			return [...buyerOrderIds].map((id) => orders.get(id)).filter((o): o is Order => Boolean(o));
		},
		getOrder: async (id: string) => {
			await sleep(80);
			requireBuyer();
			if (!buyerOrderIds.has(id) || !orders.has(id)) throw new Error("Order not found");
			return orders.get(id)!;
		},
		cancelOrder: async (id: string, reason?: string) => {
			await sleep(120);
			requireBuyer();
			const order = orders.get(id);
			if (!buyerOrderIds.has(id) || !order) throw new Error("Order not found");
			if (order.status !== "new") throw new Error("Order cannot be cancelled");
			const next: Order = {
				...order,
				status: "cancelled",
				cancelledAt: nowIso(),
				refund: { status: "mock_recorded", reason, createdAt: nowIso() },
			};
			orders.set(id, next);
			return next;
		},
		repeatOrder: async (id: string) => {
			await sleep(120);
			requireBuyer();
			const order = orders.get(id);
			if (!buyerOrderIds.has(id) || !order) throw new Error("Order not found");
			const cart: StoredCart = { id: newId("cart"), items: [], updatedAt: nowIso() };
			for (const item of order.groups.flatMap((g) => g.items)) {
				cart.items.push({
					id: newId("ci"),
					productId: item.productId,
					variantId: item.variantId,
					size: item.size,
					color: item.color,
					qty: item.qty,
				});
			}
			carts.set(cart.id, cart);
			return hydrate(cart);
		},
		reviewOrder: async (id: string, input: { rating: number; text?: string; productId?: string }) => {
			await sleep(120);
			const me = requireBuyer();
			const order = orders.get(id);
			if (!buyerOrderIds.has(id) || !order) throw new Error("Order not found");
			if (!["delivered", "completed"].includes(order.status)) throw new Error("Order can be reviewed after delivery");
			const next: Order = {
				...order,
				review: {
					id: newId("orv"),
					orderId: id,
					rating: input.rating,
					text: input.text ?? "",
					createdAt: nowIso(),
					updatedAt: nowIso(),
				},
			};
			orders.set(id, next);
			// Отзыв о конкретном товаре сразу виден в его публичной ленте (fd-review-01).
			if (input.productId) {
				const list = productReviewsStore.get(input.productId) ?? [];
				const item = order.groups.flatMap((g) => g.items).find((it) => it.productId === input.productId);
				list.unshift({
					id: newId("prv"),
					rating: input.rating,
					text: input.text ?? "",
					authorName: me.profile.name || me.user.name,
					variant: item?.variantLabel ?? item?.size ?? undefined,
					photoUrls: [],
					createdAt: nowIso(),
				});
				productReviewsStore.set(input.productId, list);
			}
			return next;
		},
		requestOrderReturn: async (id: string, input: { kind?: "return" | "dispute"; reason: string }) => {
			await sleep(120);
			requireBuyer();
			const order = orders.get(id);
			if (!buyerOrderIds.has(id) || !order) throw new Error("Order not found");
			if (!["delivered", "completed"].includes(order.status)) throw new Error("Return can be requested after delivery");
			const request = {
				id: newId("ort"),
				orderId: id,
				kind: input.kind ?? "return",
				reason: input.reason,
				status: "requested",
				createdAt: nowIso(),
				updatedAt: nowIso(),
			};
			const next: Order = {
				...order,
				status: request.kind === "dispute" ? "dispute_open" : "return_requested",
				returnRequests: [request, ...(order.returnRequests ?? [])],
			};
			orders.set(id, next);
			return next;
		},
		completeOrder: async (id: string) => {
			await sleep(120);
			requireBuyer();
			const order = orders.get(id);
			if (!buyerOrderIds.has(id) || !order) throw new Error("Order not found");
			if (!["delivered", "return_requested", "dispute_open"].includes(order.status)) throw new Error("Order cannot be completed");
			const next: Order = { ...order, status: "completed", completedAt: nowIso() };
			orders.set(id, next);
			return next;
		},
		listMessages: async () => {
			await sleep(80);
			requireBuyer();
			return buyerMessages;
		},
		listConversations: async () => {
			await sleep(80);
			requireBuyer();
			// Как на шлюзе: закреплённые — первыми, дальше по последнему сообщению.
			return [...buyerConversations.values()].sort(
				(a, b) => Number(Boolean(b.isPinned)) - Number(Boolean(a.isPinned)) || b.lastMessageAt.localeCompare(a.lastMessageAt),
			);
		},
		getConversation: async (id: string) => {
			await sleep(80);
			requireBuyer();
			const conversation = buyerConversations.get(id);
			if (!conversation) throw new Error("Conversation not found");
			return { ...conversation, unreadCount: 0 };
		},
		openSupportConversation: async (message?: string) => {
			await sleep(100);
			requireBuyer();
			// Один тред поддержки на аккаунт — как на шлюзе.
			const existing = [...buyerConversations.values()].find((c) => c.shopId === "platform");
			if (existing) return { ...existing, unreadCount: 0 };
			const conversation: ChatConversation = {
				id: newId("chat"),
				shopId: "platform",
				shopName: "Поддержка OSMIX",
				buyerUserId: mockUser.id,
				subject: "Поддержка OSMIX",
				context: { kind: "support", platform: true },
				lastMessagePreview: message?.trim() || "",
				lastMessageAt: nowIso(),
				unreadCount: 0,
				isPinned: true,
				createdAt: nowIso(),
				updatedAt: nowIso(),
				messages: [],
			};
			buyerConversations.set(conversation.id, conversation);
			return conversation;
		},
		startShopConversation: async ({ shopSlug, message, imageUris = [] }) => {
			await sleep(100);
			requireBuyer();
			const shop = mockShops.find((x) => x.slug === shopSlug);
			if (!shop) throw new Error("Shop not found");
			const existing = [...buyerConversations.values()].find((c) => c.shopId === shop.id && c.context.kind === "shop");
			const at = nowIso();
			const base: ChatConversation = existing ?? {
				id: newId("chat"),
				shopId: shop.id,
				shopName: shop.name,
				shopSlug: shop.slug,
				buyerUserId: mockUser.id,
				subject: "Вопрос магазину",
				context: { kind: "shop" },
				lastMessageAt: at,
				unreadCount: 0,
				createdAt: at,
				updatedAt: at,
				messages: [],
			};
			const chat: ChatMessage = { id: newId("chm"), conversationId: base.id, authorRole: "buyer", authorUserId: mockUser.id, body: message.trim(), imageUris, attachments: [], createdAt: at };
			const conversation = { ...base, messages: [...(base.messages ?? []), chat], lastMessageAt: at, lastMessagePreview: chat.body || "Фото", updatedAt: at };
			buyerConversations.set(conversation.id, conversation);
			return conversation;
		},
		listCustomOrders: async (shopId?: string) => {
			await sleep(80);
			requireBuyer();
			const items = [...buyerCustomOrders.values()]
				.filter((o) => !shopId || o.shopId === shopId)
				.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
			return { items };
		},
		cancelCustomOrder: async (id: string) => {
			await sleep(80);
			requireBuyer();
			const order = buyerCustomOrders.get(id);
			if (!order) throw new Error("Custom order not found");
			const next = { ...order, status: "cancelled" as const, updatedAt: nowIso() };
			buyerCustomOrders.set(id, next);
			return next;
		},
		startProductConversation: async ({ productId, message }: { productId: string; message: string }) => {
			await sleep(100);
			requireBuyer();
			const product = byId(productId);
			if (!product) throw new Error("Product not found");
			// Как на шлюзе: первое сообщение несёт карточку товара, поэтому текст
			// может быть пустым (покупатель отправил только прикреплённый товар).
			// Как на шлюзе: один тред на (покупатель, товар) — повтор дописывает в него.
			const existing = [...buyerConversations.values()].find((c) => c.productId === productId && !c.manufacturingRequestId);
			const at = nowIso();
			const base: ChatConversation = existing ?? {
				id: newId("chat"),
				shopId: product.shopId,
				shopName: product.brand,
				buyerUserId: mockUser.id,
				productId,
				productName: product.name,
				subject: product.name,
				context: { kind: "product", productName: product.name },
				lastMessageAt: at,
				unreadCount: 0,
				createdAt: at,
				updatedAt: at,
				messages: [],
			};
			const chat: ChatMessage = { id: newId("chm"), conversationId: base.id, authorRole: "buyer", authorUserId: mockUser.id, body: message.trim(), imageUris: [], attachments: [mockProductAttachment(product)], createdAt: at };
			const conversation = { ...base, messages: [...(base.messages ?? []), chat], lastMessageAt: at, lastMessagePreview: chat.body || "Товар", updatedAt: at };
			buyerConversations.set(conversation.id, conversation);
			return conversation;
		},
		replyConversation: async (id: string, body: string, imageUris?: string[], brief?: { productId?: string }) => {
			await sleep(100);
			requireBuyer();
			const conversation = buyerConversations.get(id);
			if (!conversation) throw new Error("Conversation not found");
			const attached = brief?.productId ? byId(brief.productId) : undefined;
			if (!body.trim() && !imageUris?.length && !attached) throw new Error("Message required");
			const message: ChatMessage = { id: newId("chm"), conversationId: id, authorRole: "buyer", authorUserId: mockUser.id, body: body.trim(), imageUris: imageUris ?? [], ...(attached ? { attachments: [mockProductAttachment(attached)] } : {}), createdAt: nowIso() };
			buyerConversations.set(id, { ...conversation, messages: [...(conversation.messages ?? []), message], lastMessageAt: message.createdAt, updatedAt: message.createdAt });
			return message;
		},
		markConversationRead: async (id: string) => {
			await sleep(40);
			requireBuyer();
			const conversation = buyerConversations.get(id);
			if (!conversation) throw new Error("Conversation not found");
			buyerConversations.set(id, { ...conversation, unreadCount: 0 });
		},
		pinConversations: async (ids: string[], pinned: boolean) => {
			await sleep(60);
			requireBuyer();
			let updated = 0;
			for (const id of ids) {
				const conversation = buyerConversations.get(id);
				if (!conversation) continue;
				buyerConversations.set(id, { ...conversation, isPinned: pinned });
				updated += 1;
			}
			return { updated };
		},
		deleteConversations: async (ids: string[]) => {
			await sleep(60);
			requireBuyer();
			let updated = 0;
			for (const id of ids) if (buyerConversations.delete(id)) updated += 1;
			return { updated };
		},
		uploadChatImage: async (file: File) => {
			await sleep(120);
			requireBuyer();
			return { url: URL.createObjectURL(file) };
		},
		acceptOffer: async (offerId: string, cartId: string) => {
			await sleep(120);
			requireBuyer();
			// Отклик из запроса, если принимают его: закрываем запрос и метим отклик.
			for (const [id, req] of buyerRequests) {
				const offer = req.offers.find((o) => o.id === offerId);
				if (!offer) continue;
				buyerRequests.set(id, {
					...req,
					status: "closed",
					editable: false,
					updatedAt: nowIso(),
					offers: req.offers.map((o) =>
						o.id === offerId ? { ...o, status: "accepted", acceptedAt: nowIso(), updatedAt: nowIso() } : o,
					),
				});
				break;
			}
			return hydrate(mustCart(cartId));
		},
		listRequests: async (): Promise<BuyerRequestList> => {
			await sleep(80);
			requireBuyer();
			const items = [...buyerRequests.values()]
				.map(({ offers: _offers, ...rest }) => rest)
				.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
			return { items, total: items.length };
		},
		getRequest: async (id: string): Promise<BuyerRequestDetail> => {
			await sleep(80);
			requireBuyer();
			const request = buyerRequests.get(id);
			if (!request) throw new Error("Request not found");
			return request;
		},
		createRequest: async (input: CreateBuyerRequest): Promise<BuyerRequestDetail> => {
			await sleep(140);
			const me = requireBuyer();
			const now = nowIso();
			const request: BuyerRequestDetail = {
				id: newId("req"),
				humanCode: nextRequestCode(),
				buyerUserId: me.user.id,
				type: input.type,
				categoryId: input.categoryId,
				categoryName: mockCategoryName(input.categoryId),
				title: input.title?.trim() || input.description.slice(0, 60),
				description: input.description,
				deliveryRegion: input.deliveryRegion?.trim() ?? "",
				budgetMin: input.budgetMin,
				budgetMax: input.budgetMax,
				deadline: input.deadline?.trim() || undefined,
				status: "open",
				attachments: [],
				offerCount: 0,
				editable: true,
				createdAt: now,
				updatedAt: now,
				offers: [],
			};
			buyerRequests.set(request.id, request);
			return request;
		},
		updateRequest: async (id: string, input: UpdateBuyerRequest): Promise<BuyerRequestDetail> => {
			await sleep(120);
			requireBuyer();
			const request = buyerRequests.get(id);
			if (!request) throw new Error("Request not found");
			if (!request.editable) throw new Error("Request is no longer editable");
			const next: BuyerRequestDetail = {
				...request,
				type: input.type ?? request.type,
				categoryId: input.categoryId ?? request.categoryId,
				categoryName: input.categoryId ? mockCategoryName(input.categoryId) : request.categoryName,
				title: input.title ?? request.title,
				description: input.description ?? request.description,
				deliveryRegion: input.deliveryRegion ?? request.deliveryRegion,
				budgetMin: input.budgetMin === null ? undefined : input.budgetMin ?? request.budgetMin,
				budgetMax: input.budgetMax === null ? undefined : input.budgetMax ?? request.budgetMax,
				deadline: input.deadline === null ? undefined : input.deadline ?? request.deadline,
				updatedAt: nowIso(),
			};
			buyerRequests.set(id, next);
			return next;
		},
		cancelRequest: async (id: string): Promise<BuyerRequestDetail> => {
			await sleep(100);
			requireBuyer();
			const request = buyerRequests.get(id);
			if (!request) throw new Error("Request not found");
			const next: BuyerRequestDetail = {
				...request,
				status: "cancelled",
				editable: false,
				updatedAt: nowIso(),
			};
			buyerRequests.set(id, next);
			return next;
		},
		addRequestAttachment: async (id: string, file: File): Promise<BuyerRequestAttachment> => {
			await sleep(120);
			requireBuyer();
			const request = buyerRequests.get(id);
			if (!request) throw new Error("Request not found");
			const attachment: BuyerRequestAttachment = {
				id: newId("att"),
				url: URL.createObjectURL(file),
				kind: file.type.startsWith("image/") ? "image" : "file",
				createdAt: nowIso(),
			};
			buyerRequests.set(id, {
				...request,
				attachments: [...request.attachments, attachment],
				updatedAt: nowIso(),
			});
			return attachment;
		},
		declineOffer: async (offerId: string): Promise<RequestOffer> => {
			await sleep(100);
			requireBuyer();
			for (const [id, req] of buyerRequests) {
				const offer = req.offers.find((o) => o.id === offerId);
				if (!offer) continue;
				const declined: RequestOffer = { ...offer, status: "declined", updatedAt: nowIso() };
				buyerRequests.set(id, {
					...req,
					offers: req.offers.map((o) => (o.id === offerId ? declined : o)),
					updatedAt: nowIso(),
				});
				return declined;
			}
			throw new Error("Offer not found");
		},
		acceptOfferInChat: async (offerId: string): Promise<RequestOffer> => {
			await sleep(100);
			requireBuyer();
			for (const [id, req] of buyerRequests) {
				const offer = req.offers.find((o) => o.id === offerId);
				if (!offer) continue;
				const accepted: RequestOffer = { ...offer, status: "accepted", acceptedAt: nowIso(), updatedAt: nowIso() };
				buyerRequests.set(id, {
					...req,
					status: "closed",
					offers: req.offers.map((o) => (o.id === offerId ? accepted : o)),
					updatedAt: nowIso(),
				});
				return accepted;
			}
			throw new Error("Offer not found");
		},
		listProductLikes: async (input) => {
			await sleep(80);
			requireBuyer();
			return paginateIds([...buyerProductLikes], input);
		},
		listShopLikes: async (input) => {
			await sleep(80);
			requireBuyer();
			return paginateIds([...buyerShopLikes], input);
		},
		listIdeaLikes: async (input) => {
			await sleep(80);
			requireBuyer();
			return paginateIds([...buyerIdeaLikes], input);
		},
		setProductLiked: async (productId: string, liked: boolean) => {
			await sleep(60);
			requireBuyer();
			if (liked) buyerProductLikes.add(productId);
			else buyerProductLikes.delete(productId);
			return { liked, likes: countProductLikes(productId) };
		},
		setShopLiked: async (shopId: string, liked: boolean) => {
			await sleep(60);
			requireBuyer();
			if (liked) buyerShopLikes.add(shopId);
			else buyerShopLikes.delete(shopId);
			return { liked, likes: countShopLikes(shopId) };
		},
		setReviewLiked: async (reviewId: string, liked: boolean) => {
			await sleep(60);
			requireBuyer();
			if (liked) buyerReviewLikes.add(reviewId);
			else buyerReviewLikes.delete(reviewId);
			return { liked, likes: buyerReviewLikes.has(reviewId) ? 1 : 0 };
		},
		askQuestion: async (productId: string, body: string) => {
			await sleep(80);
			requireBuyer();
			const text = body.trim().slice(0, 2000);
			if (!text) throw new Error("Напишите вопрос");
			const question = {
				id: `pqs_mock_${Date.now()}`,
				productId,
				body: text,
				authorName: "Вы",
				status: "open" as const,
				createdAt: new Date().toISOString(),
			};
			productQuestionsStore.set(productId, [question, ...(productQuestionsStore.get(productId) ?? [])]);
			return question;
		},
		setIdeaLiked: async (ideaId: string, liked: boolean) => {
			await sleep(60);
			requireBuyer();
			if (liked) buyerIdeaLikes.add(ideaId);
			else buyerIdeaLikes.delete(ideaId);
			return { liked, likes: countIdeaLikes(ideaId) };
		},
		listHiddenIdeas: async () => {
			await sleep(60);
			requireBuyer();
			return { ids: [...buyerHiddenIdeas] };
		},
		setIdeaHidden: async (ideaId: string, hidden: boolean) => {
			await sleep(60);
			requireBuyer();
			if (hidden) buyerHiddenIdeas.add(ideaId);
			else buyerHiddenIdeas.delete(ideaId);
			return { hidden };
		},
		importGuestLikes: async (clientId: string): Promise<LikeState> => {
			await sleep(120);
			requireBuyer();
			for (const id of setFor(likedProducts, clientId)) buyerProductLikes.add(id);
			for (const id of setFor(likedShops, clientId)) buyerShopLikes.add(id);
			for (const id of setFor(likedIdeas, clientId)) buyerIdeaLikes.add(id);
			likedProducts.delete(clientId);
			likedShops.delete(clientId);
			likedIdeas.delete(clientId);
			return { productIds: [...buyerProductLikes], shopIds: [...buyerShopLikes], ideaIds: [...buyerIdeaLikes] };
		},
		attachDevice: async ({ clientId, cartId }): Promise<DeviceAttachResult> => {
			await sleep(120);
			requireBuyer();
			if (clientId) {
				for (const id of setFor(likedProducts, clientId)) buyerProductLikes.add(id);
				for (const id of setFor(likedShops, clientId)) buyerShopLikes.add(id);
				for (const id of setFor(likedIdeas, clientId)) buyerIdeaLikes.add(id);
				likedProducts.delete(clientId);
				likedShops.delete(clientId);
				likedIdeas.delete(clientId);
			}
			// Мок держит одну корзину на устройство — «слияние» сводится к тому,
			// что корзина устройства и есть корзина аккаунта.
			const stored = cartId ? carts.get(cartId) : undefined;
			return {
				cart: stored ? hydrate(stored) : null,
				likes: { productIds: [...buyerProductLikes], shopIds: [...buyerShopLikes], ideaIds: [...buyerIdeaLikes] },
				viewsImported: 0,
			};
		},
	},
	notifications: {
		list: async (query) => {
			requireBuyer();
			return mockNotifications.list(query);
		},
		unreadCount: async () => {
			requireBuyer();
			return mockNotifications.unreadCount();
		},
		detail: async (id, locale) => {
			requireBuyer();
			return mockNotifications.detail(id, locale);
		},
		markRead: async (id) => {
			requireBuyer();
			return mockNotifications.markRead(id);
		},
		markAllRead: async () => {
			requireBuyer();
			return mockNotifications.markAllRead();
		},
		markReadBySource: async (request) => {
			requireBuyer();
			return mockNotifications.markReadBySource(request);
		},
		getPreferences: async () => {
			requireBuyer();
			return mockNotifications.getPreferences();
		},
		updatePreferences: async (patch) => {
			requireBuyer();
			return mockNotifications.updatePreferences(patch);
		},
		getTopicSettings: async () => {
			requireBuyer();
			return mockNotifications.getTopicSettings();
		},
		updateTopicSettings: async (patch) => {
			requireBuyer();
			return mockNotifications.updateTopicSettings(patch);
		},
		topicCounts: async (filter) => {
			requireBuyer();
			return mockNotifications.topicCounts(filter);
		},
		archive: async (id) => {
			requireBuyer();
			return mockNotifications.archive(id);
		},
		archiveAll: async (topics) => {
			requireBuyer();
			return mockNotifications.archiveAll(topics);
		},
		telegramStatus: async () => {
			requireBuyer();
			return mockNotifications.telegramStatus();
		},
		telegramLink: async () => {
			requireBuyer();
			return mockNotifications.telegramLink();
		},
		telegramUnlink: async () => {
			requireBuyer();
			return mockNotifications.telegramUnlink();
		},
	},
	support: {
		faq: async (category?: string): Promise<FaqList> => {
			await sleep(80);
			if (!category) return mockFaq;
			return { groups: mockFaq.groups.filter((g) => g.category === category) };
		},
		listTickets: async (): Promise<SupportTicket[]> => {
			await sleep(80);
			requireBuyer();
			return [...supportTickets.values()]
				.map(supportSummary)
				.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
		},
		getTicket: async (id: string): Promise<SupportTicketDetail> => {
			await sleep(80);
			requireBuyer();
			const ticket = supportTickets.get(id);
			if (!ticket) throw new Error("Обращение не найдено");
			return supportDetail(ticket);
		},
		createTicket: async (input: CreateTicketRequest): Promise<SupportTicketDetail> => {
			await sleep(140);
			const me = requireBuyer();
			const now = nowIso();
			const id = newId("tkt");
			const message: SupportTicketMessage = {
				id: newId("tmsg"),
				ticketId: id,
				authorUserId: me.user.id,
				authorRole: "user",
				authorName: me.profile.name || me.user.name,
				body: input.message,
				// Вложений в офлайн-обращении нет: класть их некуда, а поле
				// обязательное — пустой список честнее выдуманного файла.
				attachments: [],
				createdAt: now,
			};
			const ticket: StoredTicket = {
				id,
				// Номер обращения — тот, что покупатель диктует поддержке.
				// Офлайн нумеруем по порядку создания в этой вкладке.
				number: supportTickets.size + 1,
				userId: me.user.id,
				userName: me.profile.name || me.user.name,
				subject: input.subject,
				category: input.category || "general",
				status: "open",
				createdAt: now,
				updatedAt: now,
				messages: [message],
			};
			supportTickets.set(id, ticket);
			return supportDetail(ticket);
		},
		addMessage: async (id: string, body: string): Promise<SupportTicketDetail> => {
			await sleep(120);
			const me = requireBuyer();
			const ticket = supportTickets.get(id);
			if (!ticket) throw new Error("Обращение не найдено");
			const now = nowIso();
			ticket.messages.push({
				id: newId("tmsg"),
				ticketId: id,
				authorUserId: me.user.id,
				authorRole: "user",
				authorName: me.profile.name || me.user.name,
				body,
				attachments: [],
				createdAt: now,
			});
			// Демо-ответ поддержки, чтобы тред выглядел как переписка.
			ticket.messages.push({
				id: newId("tmsg"),
				ticketId: id,
				authorUserId: "support-demo",
				authorRole: "support",
				authorName: "Поддержка OSMIX",
				body: "Спасибо за обращение! Мы уже разбираемся и вернёмся с ответом в ближайшее время.",
				attachments: [],
				createdAt: new Date(Date.now() + 1000).toISOString(),
			});
			ticket.status = "in_progress";
			ticket.updatedAt = now;
			supportTickets.set(id, ticket);
			return supportDetail(ticket);
		},
		closeTicket: async (id: string): Promise<SupportTicketDetail> => {
			await sleep(100);
			requireBuyer();
			const ticket = supportTickets.get(id);
			if (!ticket) throw new Error("Обращение не найдено");
			ticket.status = "closed";
			ticket.updatedAt = nowIso();
			supportTickets.set(id, ticket);
			return supportDetail(ticket);
		},
	},
	public: {
		metrika: async (): Promise<MetrikaConfig> => {
			await sleep(20);
			return mockMetrika;
		},
	},
	engagement: {
		getLikes: async (clientId) => ({
			productIds: [...setFor(likedProducts, clientId)],
			shopIds: [...setFor(likedShops, clientId)],
			ideaIds: [...setFor(likedIdeas, clientId)],
		}),
		setProductLiked: async (clientId, productId, liked) => {
			const set = setFor(likedProducts, clientId);
			if (liked) set.add(productId);
			else set.delete(productId);
			return { liked, likes: countProductLikes(productId) };
		},
		setShopLiked: async (clientId, shopId, liked) => {
			const set = setFor(likedShops, clientId);
			if (liked) set.add(shopId);
			else set.delete(shopId);
			return { liked, likes: countShopLikes(shopId) };
		},
		setIdeaLiked: async (clientId, ideaId, liked) => {
			const set = setFor(likedIdeas, clientId);
			if (liked) set.add(ideaId);
			else set.delete(ideaId);
			return { liked, likes: countIdeaLikes(ideaId) };
		},
		getHiddenIdeas: async (clientId) => ({ ids: [...setFor(hiddenIdeasByClient, clientId)] }),
		setIdeaHidden: async (clientId, ideaId, hidden) => {
			const set = setFor(hiddenIdeasByClient, clientId);
			if (hidden) set.add(ideaId);
			else set.delete(ideaId);
			return { hidden };
		},
		sendProductMessage: async ({ productId, contact = "", message }) => {
			const product = byId(productId);
			if (!product) throw new Error("Product not found");
			const createdAt = nowIso();
			const saved = {
				id: newId("msg"),
				productId,
				shopId: product.shopId,
				contact,
				message,
				createdAt,
			};
			if (currentBuyerToken && getBuyerAuthToken() === currentBuyerToken) {
				const conversation = upsertMockConversation(product, message);
				buyerMessages.unshift({ ...saved, conversationId: conversation.id, productName: product.name, authorRole: "buyer" });
				return { ...saved, conversationId: conversation.id };
			}
			return saved;
		},
		createProductManufacturingRequest: async ({ productId, clientId, contact = "", message, selectedOptions = [] }) => {
			const product = byId(productId);
			if (!product) throw new Error("Product not found");
			if (!product.customization?.enabled) throw new Error("Manufacturing requests are available only for customizable products");
			const known = new Set(product.customization.options.map((option) => option.name));
			const unknown = selectedOptions.find((option) => !known.has(option));
			if (unknown) throw new Error("Unknown customization option");
			const createdAt = nowIso();
			const shop = shopMeta.get(product.shopId);
			const saved: ProductManufacturingRequest = {
				id: newId("req"),
				productId,
				shopId: product.shopId,
				clientId,
				contact,
				message: message?.trim() || `Запрос на изготовление по товару «${product.name}»`,
				customization: {
					productName: product.name,
					sellerName: shop?.name ?? product.brand,
					options: product.customization.options,
					selectedOptions,
				},
				status: "draft",
				createdAt,
				updatedAt: createdAt,
			};
			if (currentBuyerToken && getBuyerAuthToken() === currentBuyerToken) {
				const conversation = upsertMockConversation(product, saved.message, saved.id);
				const withConversation = { ...saved, conversationId: conversation.id };
				productManufacturingRequests.unshift(withConversation);
				return withConversation;
			}
			productManufacturingRequests.unshift(saved);
			return saved;
		},
	}, 
	// Без шлюза мета-данные страницы — общие: заголовок и описание витрины.
	seo: {
		page: async (path: string) => ({
			path,
			status: 200 as const,
			title: "OSMIX - маркетплейс изделий ручной работы",
			description: "OSMIX - маркетплейс изделий ручной работы: украшения, керамика, текстиль и декор от независимых мастеров.",
			canonical: path.split("?")[0] ?? "/",
			robots: "index, follow",
			headHtml: "",
			bodyHtml: "",
			jsonLd: [],
		}),
	},
	// Без шлюза push не настроен — переключатель на витрине не показывается.
	push: {
		config: async () => ({ enabled: false, publicKey: "" }),
		subscribe: async () => undefined,
		unsubscribe: async () => undefined,
	},
};
